/**
 * Terms Agreement Modal
 * 
 * 신규 사용자 회원가입 시 약관 동의 모달
 */

import { useState } from 'react';
import { useLanguage } from '../i18n';
import { useBodyScrollLock } from '../hooks';
import type { TermsAgreement } from '../auth';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAgree: (terms: TermsAgreement) => void;
  userInfo: { name: string; email: string; picture?: string | null } | null;
  isLoading?: boolean;
}

// 약관 상세 보기 모달
function TermsDetailModal({ 
  url, 
  title, 
  onClose 
}: { 
  url: string; 
  title: string; 
  onClose: () => void; 
}) {
  return (
    <div 
      className="fixed inset-0 z-[10003] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-2xl w-full max-w-3xl mx-4 h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <h3 className="font-medium text-gray-800">{title}</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* 콘텐츠 */}
        <iframe 
          src={url} 
          className="flex-1 w-full"
          title={title}
        />
      </div>
    </div>
  );
}

export default function TermsModal({ 
  isOpen, 
  onClose, 
  onAgree, 
  userInfo,
  isLoading = false,
}: TermsModalProps) {
  const { t, lang } = useLanguage();
  
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [marketingAgreed, setMarketingAgreed] = useState(false);
  
  // 약관 상세 보기 모달 상태
  const [detailModal, setDetailModal] = useState<{ url: string; title: string } | null>(null);
  
  // 모달 열릴 때 배경 스크롤 방지
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;
  
  const canSubmit = termsAgreed && privacyAgreed;
  
  const handleAgreeAll = () => {
    setTermsAgreed(true);
    setPrivacyAgreed(true);
    setMarketingAgreed(true);
  };
  
  const handleSubmit = () => {
    if (!canSubmit) return;
    onAgree({
      terms_agreed: termsAgreed,
      privacy_agreed: privacyAgreed,
      marketing_agreed: marketingAgreed,
    });
  };

  // 약관 링크
  const termsUrl = lang === 'ko' ? '/terms/ko.html' : '/terms/en.html';
  const privacyUrl = lang === 'ko' ? '/privacy/ko.html' : '/privacy/en.html';
  const marketingUrl = lang === 'ko' ? '/marketing/ko.html' : '/marketing/en.html';

  const openDetail = (url: string, title: string) => {
    setDetailModal({ url, title });
  };

  return (
    <>
      <div className="fixed inset-0 z-[10003] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-terminal-bg border border-terminal-border rounded-lg shadow-2xl w-full max-w-md mx-4 overflow-hidden">
          {/* 헤더 */}
          <div className="px-6 py-4 border-b border-terminal-border bg-terminal-dim/30">
            <h2 className="text-lg font-semibold text-terminal-text">
              {t.terms.title}
            </h2>
            <p className="text-sm text-terminal-muted mt-1">
              {t.terms.subtitle}
            </p>
          </div>
          
          {/* 사용자 정보 */}
          {userInfo && (
            <div className="px-6 py-3 bg-terminal-dim/20 border-b border-terminal-border flex items-center gap-3">
              {userInfo.picture && (
                <img 
                  src={userInfo.picture} 
                  alt="" 
                  className="w-10 h-10 rounded-full"
                  referrerPolicy="no-referrer"
                />
              )}
              <div>
                <p className="text-terminal-text font-medium">{userInfo.name}</p>
                <p className="text-terminal-muted text-sm">{userInfo.email}</p>
              </div>
            </div>
          )}
          
          {/* 약관 체크박스 */}
          <div className="px-6 py-4 space-y-4">
            {/* 전체 동의 */}
            <label className="flex items-center gap-3 p-3 rounded border border-terminal-border hover:border-status-go/50 cursor-pointer transition-colors bg-terminal-dim/10">
              <input
                type="checkbox"
                checked={termsAgreed && privacyAgreed && marketingAgreed}
                onChange={handleAgreeAll}
                className="w-5 h-5 rounded border-terminal-border text-status-go focus:ring-status-go focus:ring-offset-0"
              />
              <span className="text-terminal-text font-medium">{t.terms.agreeAll}</span>
            </label>
            
            <div className="border-t border-terminal-border my-2"></div>
            
            {/* 이용약관 */}
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={termsAgreed}
                onChange={(e) => setTermsAgreed(e.target.checked)}
                className="w-4 h-4 rounded border-terminal-border text-status-go focus:ring-status-go focus:ring-offset-0"
              />
              <span className="flex-1 text-terminal-text text-sm">
                {t.terms.termsOfServiceRequired}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openDetail(termsUrl, t.terms.termsOfService);
                }}
                className="text-status-go text-xs hover:underline"
              >
                {t.terms.viewDetails}
              </button>
            </label>
            
            {/* 개인정보처리방침 */}
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={privacyAgreed}
                onChange={(e) => setPrivacyAgreed(e.target.checked)}
                className="w-4 h-4 rounded border-terminal-border text-status-go focus:ring-status-go focus:ring-offset-0"
              />
              <span className="flex-1 text-terminal-text text-sm">
                {t.terms.privacyPolicyRequired}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openDetail(privacyUrl, t.terms.privacyPolicy);
                }}
                className="text-status-go text-xs hover:underline"
              >
                {t.terms.viewDetails}
              </button>
            </label>
            
            {/* 마케팅 수신 동의 */}
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={marketingAgreed}
                onChange={(e) => setMarketingAgreed(e.target.checked)}
                className="w-4 h-4 rounded border-terminal-border text-status-go focus:ring-status-go focus:ring-offset-0"
              />
              <span className="flex-1 text-terminal-muted text-sm">
                {t.terms.marketingOptional}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openDetail(marketingUrl, t.terms.marketingConsent);
                }}
                className="text-status-go text-xs hover:underline"
              >
                {t.terms.viewDetails}
              </button>
            </label>
          </div>
          
          {/* 푸터 버튼 */}
          <div className="px-6 py-4 border-t border-terminal-border flex gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-terminal-border text-terminal-muted rounded hover:bg-terminal-dim/30 transition-colors disabled:opacity-50"
            >
              {t.terms.cancel}
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || isLoading}
              className="flex-1 px-4 py-2 bg-status-go text-white rounded font-medium hover:bg-status-go/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? '...' : t.terms.continue}
            </button>
          </div>
        </div>
      </div>
      
      {/* 약관 상세 보기 모달 */}
      {detailModal && (
        <TermsDetailModal
          url={detailModal.url}
          title={detailModal.title}
          onClose={() => setDetailModal(null)}
        />
      )}
    </>
  );
}
